import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X, Check, ShieldCheck, ChevronDown, Trash2 } from "lucide-react";
import { getUserBankList, bankRequestOTP, addUserBankAccount, updateUserBankAccount, deleteUserBankAccount } from "@/api/Bank";
import { message } from "antd";
import bankList from "@/data/bank_list.json";

interface BankAccount {
  id: string;
  bankName: string;
  bankCode: string;
  accountNumber: string;
  accountHolder: string;
  branchName: string;
  isVerified: boolean;
  isDefault: boolean;
}

const BankingTab: React.FC = () => {
  const { t } = useTranslation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [isBankListOpen, setIsBankListOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [deletingAccountId, setDeletingAccountId] = useState<string | null>(null);
  const [otpAction, setOtpAction] = useState<'add' | 'update' | 'delete'>('add');

  const [formData, setFormData] = useState({
    bankName: "",
    branchName: "",
    accountNumber: "",
    accountHolderName: "",
    setAsDefault: false,
  });

  const fetchBankList = () => {
    getUserBankList().then((response) => {
      setAccounts(response.data.map((bank) => ({
        id: bank.id.toString(),
        bankName: bank.bank_name,
        bankCode: bank.bank_short_name,
        accountNumber: bank.account_number,
        accountHolder: bank.account_holder_name,
        branchName: bank.branch,
        isVerified: true,
        isDefault: bank.is_default === 1,
      })));
    });
  };

  const handleEditClick = (account: BankAccount) => {
    setEditingAccountId(account.id);
    setFormData({
      bankName: account.bankName,
      branchName: account.branchName,
      accountNumber: account.accountNumber,
      accountHolderName: account.accountHolder,
      setAsDefault: account.isDefault,
    });
    setShowAddModal(true);
  };

  const handleSubmit = () => {
    if (!formData.bankName || !formData.accountNumber || !formData.accountHolderName) {
      message.warning(t("profile.banking.fillRequired", "Vui lòng nhập đầy đủ thông tin bắt buộc"));
      return;
    }

    setOtpAction(editingAccountId ? 'update' : 'add');
    setIsSubmitting(true);
    bankRequestOTP()
      .then(() => {
        setShowOtpModal(true);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleDeleteClick = (id: string) => {
    setDeletingAccountId(id);
    setOtpAction('delete');
    setIsSubmitting(true);
    bankRequestOTP()
      .then(() => {
        setShowOtpModal(true);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleConfirmOtp = () => {
    if (otp.length < 6) {
      message.warning(t("profile.banking.otpRequired", "Vui lòng nhập mã OTP 6 chữ số"));
      return;
    }

    setIsSubmitting(true);

    let apiCall;
    if (otpAction === 'delete') {
      apiCall = deleteUserBankAccount(Number(deletingAccountId), otp);
    } else if (editingAccountId) {
      apiCall = updateUserBankAccount(
        Number(editingAccountId),
        otp,
        formData.bankName,
        formData.accountNumber,
        formData.accountHolderName,
        formData.branchName,
        formData.setAsDefault,
      );
    } else {
      apiCall = addUserBankAccount(
        otp,
        formData.bankName,
        formData.accountNumber,
        formData.accountHolderName,
        formData.branchName,
        formData.setAsDefault,
      );
    }

    apiCall
      .then((response) => {
        if (response.status) {
          setShowOtpModal(false);
          setShowAddModal(false);
          setEditingAccountId(null);
          setDeletingAccountId(null);
          setOtp("");
          fetchBankList();
          // Reset form
          setFormData({
            bankName: "",
            branchName: "",
            accountNumber: "",
            accountHolderName: "",
            setAsDefault: false,
          });
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  useEffect(() => {
    fetchBankList();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsBankListOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t("profile.banking.title", "Tài khoản ngân hàng")}
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Add New Account Card */}
        <button
          onClick={() => {
            setEditingAccountId(null);
            setFormData({
              bankName: "",
              branchName: "",
              accountNumber: "",
              accountHolderName: "",
              setAsDefault: false,
            });
            setShowAddModal(true);
          }}
          className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-6 flex flex-col items-center justify-center hover:border-primary dark:hover:border-primary-light transition-colors min-h-[200px] group"
        >
          <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3 group-hover:bg-primary/10 transition-colors">
            <Plus className="w-6 h-6 text-gray-400 group-hover:text-primary dark:group-hover:text-primary-light" />
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400 group-hover:text-primary dark:group-hover:text-primary-light">
            {t("profile.banking.addAccount", "Thêm Tài khoản Ngân hàng")}
          </span>
        </button>

        {accounts.map((account) => (
          <div
            key={account.id}
            onClick={() => handleEditClick(account)}
            className="bg-gradient-to-br from-gray-700 to-gray-900 dark:from-gray-800 dark:to-gray-950 rounded-md p-6 text-white shadow-lg relative overflow-hidden cursor-pointer hover:shadow-xl transition-all"
          >
            {/* Delete button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(account.id);
              }}
              className="absolute top-4 left-4 p-2 text-white/50 hover:text-red-400 transition-colors bg-white/10 rounded-full z-10"
            >
              <Trash2 className="w-4 h-4" />
            </button>

            {/* Thay logo ở đây */}
            <div className="absolute top-4 right-4 w-12 h-12 bg-white rounded-lg flex items-center justify-center">
              <span className="text-xs font-bold text-gray-800">
                {account.bankCode}
              </span>
            </div>

            <div className="mb-4">
              {account.isVerified ? (
                <span className="inline-flex items-center gap-1 text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
                  <Check className="w-3 h-3" />
                  {t("profile.banking.verified", "Đã xác thực")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded-full">
                  {t("profile.banking.pending", "Đã kiểm tra")}
                </span>
              )}
            </div>

            <h3 className="text-sm font-medium mb-4 line-clamp-1">
              {account.bankName}
            </h3>

            <div className="mb-4">
              <p className="text-2xl font-mono tracking-wider">
                {account.accountNumber.length > 6
                  ? `${account.accountNumber.slice(0, 3)}****${account.accountNumber.slice(-3)}`
                  : account.accountNumber}
              </p>
            </div>

            <div>
              <p className="text-sm font-medium">{account.accountHolder}</p>
            </div>

            {/* Default Badge */}
            {account.isDefault && (
              <div className="absolute bottom-4 right-4">
                <span className="text-xs bg-primary/20 text-primary-light px-2 py-1 rounded-full">
                  {t("profile.banking.default", "Mặc định")}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Account Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-md max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                {editingAccountId 
                  ? t("profile.banking.editAccount", "Cập nhật tài khoản ngân hàng")
                  : t("profile.banking.addAccount", "Tài khoản ngân hàng")
                }
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-red-600 dark:hover:text-red-300 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-8 py-6 space-y-4 overflow-y-auto max-h-[calc(90vh-180px)]">

              {/* Bank Name */}
              <div className="grid grid-cols-12 gap-4 items-center">
                <label className="col-span-4 text-sm font-medium text-gray-600 dark:text-gray-400 text-right">
                  {t("profile.banking.bankName", "Tên Ngân hàng")}
                </label>
                <div className="col-span-8 relative" ref={dropdownRef}>
                  <div
                    onClick={() => setIsBankListOpen(!isBankListOpen)}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all outline-none cursor-pointer flex items-center justify-between h-[42px]"
                  >
                    <span className={!formData.bankName ? "text-gray-500 dark:text-gray-400" : ""}>
                      {formData.bankName || t('profile.banking.selectBank', 'Chọn ngân hàng')}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${isBankListOpen ? 'rotate-180' : ''}`} />
                  </div>

                  {isBankListOpen && (
                    <div className="absolute top-[calc(100%+4px)]  left-0 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-xl max-h-64 flex flex-col z-[1000]">
                      <div className="p-2 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 sticky top-0 rounded-t-md">
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder={t("common.search", "Search...")}
                          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:border-primary placeholder-gray-400 dark:placeholder-gray-500"
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>
                      <div className="overflow-y-auto no-scrollbar flex-1 bg-white dark:bg-gray-800 rounded-b-md">
                        {bankList
                          .filter(
                            (bank) =>
                              bank.bank_name.toLowerCase().includes(searchTerm.toLowerCase())
                          )
                          .map((bank) => (
                            <div
                              key={bank.no}
                              onClick={() => {
                                setFormData((prev) => ({ ...prev, bankName: bank.bank_name }));
                                setIsBankListOpen(false);
                                setSearchTerm("");
                              }}
                              className={`px-4 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-50 dark:border-gray-700/50 last:border-0 ${formData.bankName === bank.bank_name
                                  ? "bg-primary/5 text-primary dark:bg-primary/20 dark:text-primary-light"
                                  : "text-gray-700 dark:text-gray-200"
                                }`}
                            >
                              <div className="font-medium">{bank.bank_name}</div>
                            </div>
                          ))}
                        {bankList.filter((b) =>
                          b.bank_name.toLowerCase().includes(searchTerm.toLowerCase())
                        ).length === 0 && (
                            <div className="px-4 py-3 text-sm text-center text-gray-500 dark:text-gray-400">
                              No results found
                            </div>
                          )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Branch Name */}
              {/* <div className="grid grid-cols-12 gap-4 items-start">
                <label className="col-span-4 text-sm font-medium text-gray-600 dark:text-gray-400 text-right pt-3">
                  {t(
                    "profile.banking.branchName",
                    "Tên chi nhánh ngân hàng (Theo thông tin trên sao kê)",
                  )}
                </label>
                <div className="col-span-8 relative">
                  <select
                    name="branchName"
                    value={formData.branchName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary dark:bg-gray-700 dark:text-white transition-all outline-none appearance-none cursor-pointer"
                  >
                    <option value="">
                      {t("profile.banking.selectBranch", "Chọn chi nhánh")}
                    </option>
                    <option value="hcm">Chi nhánh TP.HCM</option>
                    <option value="hn">Chi nhánh Hà Nội</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div> */}

              {/* Account Number */}
              <div className="grid grid-cols-12 gap-4 items-center">
                <label className="col-span-4 text-sm font-medium text-gray-600 dark:text-gray-400 text-right">
                  {t("profile.banking.accountNumber", "Số tài khoản")}
                </label>
                <div className="col-span-8">
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={(e) => {
                      // Chỉ cho phép nhập số
                      const value = e.target.value.replace(/\D/g, '');
                      setFormData((prev) => ({ ...prev, accountNumber: value }));
                    }}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary dark:bg-gray-700 dark:text-white transition-all outline-none"
                    placeholder=""
                  />
                </div>
              </div>

              {/* Account Holder Name */}
              <div className="grid grid-cols-12 gap-4 items-start">
                <label className="col-span-4 text-sm font-medium text-gray-600 dark:text-gray-400 text-right pt-3">
                  {t(
                    "profile.banking.accountHolderName",
                    "Tên chủ tài khoản (Viết in hoa, không dấu - NGUYEN VAN A)",
                  )}
                </label>
                <div className="col-span-8">
                  <input
                    type="text"
                    name="accountHolderName"
                    value={formData.accountHolderName}
                    onChange={handleInputChange}
                    maxLength={64}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary dark:bg-gray-700 dark:text-white transition-all outline-none"
                    placeholder=""
                  />
                  <div className="text-right text-xs text-gray-400 mt-1.5">
                    {formData.accountHolderName.length}/64
                  </div>
                </div>
              </div>

              {/* Set as Default */}
              <div className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-4"></div>
                <div className="col-span-8">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      name="setAsDefault"
                      id="setAsDefault"
                      checked={formData.setAsDefault}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-primary border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary/20 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                      {t(
                        "profile.banking.setAsDefault",
                        "Set as Default Receiving Account",
                      )}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-8 py-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-8 py-3 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 rounded-md transition-all font-medium border border-gray-200 dark:border-gray-600"
              >
                {t("common.cancel", "Hủy")}
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-8 py-3 bg-gradient-to-r from-primary to-primary-dark hover:shadow-lg hover:shadow-primary/30 text-white rounded-md transition-all font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {editingAccountId ? t("common.save", "Lưu thay đổi") : t("common.continue", "Tiếp theo")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-md max-w-md w-full shadow-2xl overflow-hidden">
            <div className="px-6 py-8 text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {otpAction === 'delete' 
                  ? t("profile.banking.otpDeleteTitle", "Xác thực xóa tài khoản") 
                  : otpAction === 'update' 
                    ? t("profile.banking.otpUpdateTitle", "Xác thực cập nhật") 
                    : t("profile.banking.otpTitle", "Xác thực OTP")}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6 px-4">
                {otpAction === 'delete'
                  ? t("profile.banking.otpDeleteDesc", "Vui lòng nhập mã OTP để xác nhận xóa tài khoản ngân hàng này.")
                  : otpAction === 'update'
                    ? t("profile.banking.otpUpdateDesc", "Vui lòng nhập mã OTP để xác nhận cập nhật thông tin tài khoản ngân hàng.")
                    : t("profile.banking.otpDesc", "Vui lòng nhập mã OTP đã được gửi đến số điện thoại/email của bạn để xác nhận thêm tài khoản ngân hàng.")
                }
              </p>

              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="------"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                  className="w-full px-4 py-3 border-2 border-gray-100 dark:border-gray-700 rounded-lg text-center text-3xl font-mono tracking-[0.5em] focus:border-primary outline-none transition-all dark:bg-gray-700 dark:text-white"
                />

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowOtpModal(false)}
                    className="flex-1 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all font-medium"
                  >
                    {t("common.cancel", "Hủy")}
                  </button>
                  <button
                    onClick={handleConfirmOtp}
                    disabled={otp.length < 6 || isSubmitting}
                    className="flex-1 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg transition-all font-medium disabled:opacity-50 shadow-lg shadow-primary/20 flex items-center justify-center gap-2"
                  >
                    {isSubmitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    {t("common.confirm", "Xác nhận")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankingTab;
